import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, retry, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { MessageService } from '@letly/ui-common';
import { ToastSeverity } from '@letly/models';
import { NotificationService } from './notification.service';
import { NotificationActions } from './notification.actions';
import { selectPreferences } from './notification.reducer';

@Injectable()
export class NotificationEffect {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private service = inject(NotificationService);
  private messageService = inject(MessageService);

  loadPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.loadPreferences),
      switchMap(() =>
        this.service.getPreferences().pipe(
          map((res) =>
            res.success
              ? NotificationActions.loadPreferencesSuccess({ preferences: res.data })
              : NotificationActions.loadPreferencesFailure({ error: res.niceErrorMessage })
          ),
          catchError((error) =>
            of(NotificationActions.loadPreferencesFailure({ error: error.message }))
          )
        )
      )
    )
  );

  syncOnToggle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.toggleGlobalEmail, NotificationActions.toggleCategoryEmail),
      withLatestFrom(this.store.select(selectPreferences)),
      switchMap(([action, preferences]) => {
        if (!preferences) return of(NotificationActions.updatePreferencesFailure({ error: $localize`:@@account.notifications.error.no-preferences:No preferences loaded` }));
        const patch = action.type === NotificationActions.toggleGlobalEmail.type
          ? { isEmailEnabled: preferences.isEmailEnabled }
          : {
            categories: [{
              key: action.categoryKey,
              isEmailEnabled: preferences.categories.find((c) => c.key === action.categoryKey)?.isEmailEnabled
            }]
          };
        return this.service.updatePreferences(patch).pipe(
          retry(1),
          map((res) =>
            res.success
              ? NotificationActions.updatePreferencesSuccess({ preferences: res.data })
              : NotificationActions.updatePreferencesFailure({ error: res.niceErrorMessage })
          ),
          catchError((error) =>
            of(NotificationActions.updatePreferencesFailure({ error: error.message }))
          )
        );
      })
    )
  );

  updatePreferencesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationActions.updatePreferencesSuccess),
        tap(() => {
          this.messageService.showSnackbar($localize`:@@account.notifications.toast.saved:Preferences saved`, ToastSeverity.Success);
        })
      ),
    { dispatch: false }
  );

  showError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          NotificationActions.loadPreferencesFailure,
          NotificationActions.updatePreferencesFailure
        ),
        tap(({ error }) => {
          this.messageService.showSnackbar(error, ToastSeverity.Error);
        })
      ),
    { dispatch: false }
  );
}
